/**
 * Capability contract (frontend mirror) — the single source of truth for
 * "what may this account do" on the client.
 *
 * IMPORTANT: this table is mirrored verbatim from
 * `backend/utilities/capabilities.js`. The backend is the enforcer; this copy
 * drives UX only (hiding create buttons, filtering nav tabs, route guards).
 * Keep the two structurally identical so they cannot drift silently — if you
 * change a rule here, change it there in the same PR.
 */

export type AccountType = 'pro' | 'community' | 'trial'

export interface Capabilities {
	/** Create locations / projects / portfolios. */
	canCreateAssets: boolean
	/** Create notes, images, documents, chat messages. */
	canWrite: boolean
	/** Read the financial subsystems. */
	canSeeFinancials: boolean
	/** Bypass the per-document ACL (see every document). */
	canSeeAllDocuments: boolean
	/**
	 * Reach AI Assist (MYD-606). Kept while impersonating; the server refuses
	 * impersonated execution. Show AI UI only when the runtime config also says
	 * AI is enabled for this account type.
	 */
	canUseAi: boolean
	/** This session is a support impersonation (read-only). */
	isImpersonating: boolean
}

interface CapabilityUser {
	accountType?: AccountType | string
	isImpersonating?: boolean
}

const TIERS: Record<AccountType, Omit<Capabilities, 'isImpersonating'>> = {
	pro: {
		canCreateAssets: true,
		canWrite: true,
		canSeeFinancials: true,
		canSeeAllDocuments: false, // pro sees own + partnered docs via the ACL
		canUseAi: true,
	},
	community: {
		canCreateAssets: false,
		canWrite: true, // notes / images / documents / chat allowed
		canSeeFinancials: false,
		canSeeAllDocuments: false, // granted documents only, via the ACL
		canUseAi: false,
	},
	trial: {
		canCreateAssets: false,
		canWrite: true,
		canSeeFinancials: true, // full visibility for demo/marketing
		canSeeAllDocuments: true, // sees every document
		// A demo account; AI spends real money per call.
		canUseAi: false,
	},
}

export const getCapabilities = (user?: CapabilityUser | null): Capabilities => {
	const accountType = (user?.accountType as AccountType) || 'pro'
	const isImpersonating = Boolean(user?.isImpersonating)
	const tier = TIERS[accountType] || TIERS.pro

	return {
		...tier,
		// Read-only override for impersonation — no writes of any kind.
		canCreateAssets: tier.canCreateAssets && !isImpersonating,
		canWrite: tier.canWrite && !isImpersonating,
		isImpersonating,
	}
}

export default getCapabilities

/* -------------------------------------------------------------------------
 * Per-asset access levels
 *
 * Mirror of `backend/utilities/assetAccess.js`. Everything above this line is
 * the verbatim mirror of `backend/utilities/capabilities.js`; keep it that
 * way and add asset-scoped logic here instead.
 * ---------------------------------------------------------------------- */

export type PartnerAccessLevel = 'full' | 'readonly'

/**
 * Stored levels can be missing on rows written before access levels existed
 * (and on any `.lean()` read that bypassed the schema default), so anything
 * that isn't explicitly 'readonly' reads as 'full'.
 */
export const normalizeAccessLevel = (value?: string | null): PartnerAccessLevel =>
	value === 'readonly' ? 'readonly' : 'full'

/** Find the viewer's entry in an asset's partners[], populated or raw. */
export const findPartnerEntry = (asset: any, userId?: string): any =>
	(asset?.partners || []).find((entry: any) => {
		const id = entry?.partnerUserId?._id ?? entry?.partnerUserId
		return id && String(id) === String(userId)
	}) || null

/**
 * Narrow account capabilities by a per-asset access level.
 *
 * INTERSECTION, never a union: the account tier is a ceiling that a grant can
 * lower but never raise. A community account granted 'full' still cannot see
 * financials. Mirrors resolveAssetAccess on the backend, which is the actual
 * enforcer — this copy only decides what to show.
 */
export const applyAccessLevel = (
	capabilities: Capabilities,
	accessLevel?: string | null,
): Capabilities =>
	normalizeAccessLevel(accessLevel) === 'readonly'
		? {
				...capabilities,
				canCreateAssets: false,
				canSeeFinancials: false,
				canSeeAllDocuments: false,
			}
		: capabilities

/**
 * The viewer's effective capabilities on one asset. Owners and non-partners
 * both keep their plain account capabilities.
 */
export const getAssetCapabilities = (user: any, asset: any): Capabilities => {
	const capabilities = getCapabilities(user)
	if (!asset || !user?._id) return capabilities

	const ownerId = asset.owner?._id ?? asset.owner
	if (ownerId && String(ownerId) === String(user._id)) return capabilities

	const entry = findPartnerEntry(asset, user._id)
	if (!entry) return capabilities

	return applyAccessLevel(capabilities, entry.accessLevel)
}
